/**
 * useImageLoading Hook
 *
 * Manages image loading functionality including:
 * - loadedImages state management
 * - Image preloading logic
 * - Placeholder handling
 * - S3 URL signing
 * - Image loading error handling
 *
 * TODO: Extract loadedImages state management
 * TODO: Move image preloading and onLoad handlers
 * TODO: Extract placeholder image logic
 * TODO: Create S3 URL signing utilities
 * TODO: Add image loading error handling
 */

import { useState, useCallback } from 'react'
import placeholderImg from '../../../assets/images/camera.jpg'
import placeholderNavImg from '../../../assets/images/camera-icon-bg2.jpg'

export const useImageLoading = (type = 'asset') => {
  const [loadedImages, setLoadedImages] = useState({})
  const [imageErrors, setImageErrors] = useState({})
  const [loadingImages, setLoadingImages] = useState({})

  // Select appropriate placeholder based on context
  const getPlaceholderImage = useCallback(() => {
    return type === 'navigation' ? placeholderNavImg : placeholderImg
  }, [type])

  // Handle successful image load
  const handleImageLoad = useCallback(imageId => {
    setLoadedImages(prevState => ({
      ...prevState,
      [imageId]: true
    }))
    setLoadingImages(prevState => {
      const newState = { ...prevState }
      delete newState[imageId]
      return newState
    })
    // Clear any previous errors for this image
    setImageErrors(prevState => {
      const newState = { ...prevState }
      delete newState[imageId]
      return newState
    })
  }, [])

  // Handle image load error
  const handleImageError = useCallback((imageId, error) => {
    console.warn(`Failed to load image ${imageId}:`, error)
    setImageErrors(prevState => ({
      ...prevState,
      [imageId]: error || 'Failed to load image'
    }))
    setLoadingImages(prevState => {
      const newState = { ...prevState }
      delete newState[imageId]
      return newState
    })
  }, [])

  // Start loading an image
  const startImageLoad = useCallback(
    imageId => {
      if (!loadedImages[imageId] && !loadingImages[imageId]) {
        setLoadingImages(prevState => ({
          ...prevState,
          [imageId]: true
        }))
      }
    },
    [loadedImages, loadingImages]
  )

  // Preload an image
  const preloadImage = useCallback(
    (imageId, src) => {
      return new Promise((resolve, reject) => {
        if (loadedImages[imageId]) {
          resolve(src)
          return
        }

        startImageLoad(imageId)

        const img = new Image()
        img.onload = () => {
          handleImageLoad(imageId)
          resolve(src)
        }
        img.onerror = error => {
          handleImageError(imageId, 'Image failed to preload')
          reject(error)
        }
        img.src = src
      })
    },
    [loadedImages, startImageLoad, handleImageLoad, handleImageError]
  )

  // Batch preload multiple images
  const preloadImages = useCallback(
    images => {
      const promises = images.map(({ id, src }) =>
        preloadImage(id, src).catch(error => ({ id, error }))
      )
      return Promise.allSettled(promises)
    },
    [preloadImage]
  )

  // Get the appropriate image source (loaded image or placeholder)
  const getImageSrc = useCallback(
    (imageId, originalSrc) => {
      if (imageErrors[imageId]) {
        return getPlaceholderImage()
      }
      return loadedImages[imageId] ? originalSrc : getPlaceholderImage()
    },
    [loadedImages, imageErrors, getPlaceholderImage]
  )

  // Get image loading state
  const getImageState = useCallback(
    imageId => {
      return {
        isLoaded: !!loadedImages[imageId],
        isLoading: !!loadingImages[imageId],
        hasError: !!imageErrors[imageId],
        error: imageErrors[imageId]
      }
    },
    [loadedImages, loadingImages, imageErrors]
  )

  // Get style properties for background images
  const getImageStyles = useCallback(
    (imageId, originalSrc, additionalStyles = {}) => {
      const isLoaded = loadedImages[imageId]
      const hasError = imageErrors[imageId]
      const src = getImageSrc(imageId, originalSrc)

      return {
        backgroundImage: `url('${src}')`,
        backgroundSize: isLoaded && !hasError ? 'cover' : '100%',
        backgroundRepeat: 'no-repeat',
        backgroundColor: isLoaded && !hasError ? 'transparent' : '#D9D9D9',
        ...additionalStyles
      }
    },
    [loadedImages, imageErrors, getImageSrc]
  )

  // Create onLoad handler for hidden img elements
  const createOnLoadHandler = useCallback(
    imageId => {
      return () => handleImageLoad(imageId)
    },
    [handleImageLoad]
  )

  // Create onError handler for hidden img elements
  const createOnErrorHandler = useCallback(
    imageId => {
      return event => handleImageError(imageId, 'Image load failed')
    },
    [handleImageError]
  )

  // Reset loading states (useful for cleanup)
  const resetImageStates = useCallback(() => {
    setLoadedImages({})
    setImageErrors({})
    setLoadingImages({})
  }, [])

  // Remove specific image from states
  const clearImageState = useCallback(imageId => {
    setLoadedImages(prevState => {
      const newState = { ...prevState }
      delete newState[imageId]
      return newState
    })
    setImageErrors(prevState => {
      const newState = { ...prevState }
      delete newState[imageId]
      return newState
    })
    setLoadingImages(prevState => {
      const newState = { ...prevState }
      delete newState[imageId]
      return newState
    })
  }, [])

  return {
    // State
    loadedImages,
    imageErrors,
    loadingImages,

    // Core functions
    handleImageLoad,
    handleImageError,
    preloadImage,
    preloadImages,
    getImageSrc,
    getImageState,
    getImageStyles,
    getPlaceholderImage,

    // Handler creators
    createOnLoadHandler,
    createOnErrorHandler,

    // Utility functions
    resetImageStates,
    clearImageState,
    startImageLoad
  }
}

export default useImageLoading
